import { useEffect, useRef } from "react";
import { dayWindow } from "@yb/core";
import { hhmm } from "../lib/format";

export const MIN = 60_000;
export const LABEL_W = 176;

export interface Scale {
  from: number;
  to: number;
  ppm: number;
  width: number;
  x: (ms: number) => number;
  hours: number[];
}

/** Hours shown on either side of the day, so an overnight stay is visible at both ends. */
export const EDGE_HOURS = 6;

/**
 * What the board spans: the operating day, plus a margin at each end. Without it an
 * aircraft that arrived last night starts off-screen and cannot be scrolled to.
 */
export function boardWindow(date: string, timezone: string): { from: number; to: number } {
  const day = dayWindow(date, timezone);
  return { from: day.from - EDGE_HOURS * 3_600_000, to: day.to + EDGE_HOURS * 3_600_000 };
}

export function makeScale(from: number, to: number, ppm: number): Scale {
  const hours: number[] = [];
  for (let h = from; h <= to; h += 60 * MIN) hours.push(h);
  return { from, to, ppm, width: ((to - from) / MIN) * ppm, x: (ms) => ((ms - from) / MIN) * ppm, hours };
}

export function TimeHeader({ scale, now, tz, label }: { scale: Scale; now: number; tz: string; label: string }) {
  return (
    <div className="sticky top-0 z-50 flex h-8 border-b border-line bg-panel/95 backdrop-blur">
      <div
        className="sticky left-0 z-10 flex shrink-0 items-center border-r border-line bg-panel px-3 text-[11px] font-medium text-ink-3"
        style={{ width: LABEL_W }}
      >
        {label}
      </div>
      <div className="relative" style={{ width: scale.width }}>
        {scale.hours.slice(0, -1).map((h) => (
          <div key={h} className="tabular absolute top-0 flex h-full items-center pl-1.5 text-[11px] text-ink-3" style={{ left: scale.x(h) }}>
            {hhmm(h, tz)}
          </div>
        ))}
        {now >= scale.from && now <= scale.to && (
          <div
            className="tabular absolute top-1 z-10 -translate-x-1/2 rounded bg-bad px-1.5 py-0.5 text-[10px] font-bold whitespace-nowrap text-white shadow"
            style={{ left: scale.x(now) }}
          >
            Now {hhmm(now, tz)}
          </div>
        )}
      </div>
    </div>
  );
}

export function GridLines({ scale }: { scale: Scale }) {
  return (
    <div className="pointer-events-none absolute inset-0">
      {scale.hours.map((h, i) => (
        <div
          key={h}
          className="absolute top-0 bottom-0 border-l"
          style={{ left: scale.x(h), borderColor: i % 3 === 0 ? "var(--grid-strong)" : "var(--grid)" }}
        />
      ))}
    </div>
  );
}

/** The current time: a red line over the bars, with the past lightly shaded. */
export function NowLine({ scale, now }: { scale: Scale; now: number }) {
  if (now < scale.from || now > scale.to) return null;
  const x = scale.x(now);
  return (
    <>
      <div className="pointer-events-none absolute top-0 bottom-0 left-0 z-0 bg-ink/[0.035]" style={{ width: x }} />
      <div className="pointer-events-none absolute top-0 bottom-0 z-30 w-[2px] -translate-x-1/2 bg-bad shadow-[0_0_6px_rgba(220,38,38,0.55)]" style={{ left: x }} />
    </>
  );
}

/** Scroll so "now" (or a focused time) sits a third of the way in, once per day/zoom. */
export function useAutoScroll(el: React.RefObject<HTMLDivElement | null>, scale: Scale, now: number, key: string, focusAt: number | null) {
  const done = useRef("");
  useEffect(() => {
    const node = el.current;
    if (!node) return;
    if (done.current === key && focusAt === null) return;
    done.current = key;
    const inDay = (t: number | null): t is number => t != null && t >= scale.from && t <= scale.to;
    const target = inDay(focusAt) ? focusAt : inDay(now) ? now : scale.from + 6 * 60 * MIN;
    node.scrollTo({ left: Math.max(0, scale.x(target) - (node.clientWidth - LABEL_W) / 3), behavior: focusAt ? "smooth" : "auto" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, focusAt]);
}

/** Greedy lane packing for items that may overlap within one row. */
export function packLanes<T extends { from: number; to: number }>(items: T[]): Array<T & { lane: number }> {
  const lanesEnd: number[] = [];
  return [...items]
    .sort((a, b) => a.from - b.from)
    .map((it) => {
      let lane = lanesEnd.findIndex((end) => end <= it.from);
      if (lane === -1) {
        lane = lanesEnd.length;
        lanesEnd.push(it.to);
      } else lanesEnd[lane] = it.to;
      return { ...it, lane };
    });
}
